import { Injectable, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';

/**
 * Service for displaying user notifications (success, error, info, warning)
 * Uses Material Snackbar for consistent notification UI
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly snackBar = inject(MatSnackBar);

  private readonly defaultErrorConfig: MatSnackBarConfig = {
    duration: 5000,
    horizontalPosition: 'center',
    verticalPosition: 'top',
    panelClass: ['error-snackbar'],
  };

  /**
   * Show a single error message
   */
  showError(message: string, duration?: number): void {
    this.snackBar.open(message, 'Close', {
      ...this.defaultErrorConfig,
      duration: duration ?? this.defaultErrorConfig.duration,
    });
  }

  /**
   * Show multiple error messages (from validation errors)
   */
  showErrors(errors: string[], duration?: number): void {
    const message = errors.join('\n');
    this.snackBar.open(message, 'Close', {
      ...this.defaultErrorConfig,
      duration: duration ?? this.defaultErrorConfig.duration,
    });
  }

  /**
   * Show success message
   */
  showSuccess(message: string, duration = 5000): void {
    this.snackBar.open(message, 'Close', {
      duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['success-snackbar'],
    });
  }

  /**
   * Show info message
   */
  showInfo(message: string, duration = 5000): void {
    this.snackBar.open(message, 'Close', {
      duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['info-snackbar'],
    });
  }

  /**
   * Show warning message
   */
  showWarning(message: string, duration = 5000): void {
    this.snackBar.open(message, 'Close', {
      duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['warning-snackbar'],
    });
  }

  /**
   * Dismiss all active snackbars
   */
  dismiss(): void {
    this.snackBar.dismiss();
  }
}
